import { Check, Loader2, Settings2 } from "lucide-react";
import {
	AccordionHeader,
	AccordionItem,
	AccordionPanel,
	AccordionTrigger,
} from "@/components/Accordion";
import { Field, InfoLabel } from "@/components/catalyst/Fieldset";
import { Input, InputGroup } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import type {
	AndroidStoreLinkSection,
	AttributionModeSection,
	CorsOriginsSection,
	IosStoreLinkSection,
} from "../types";

interface GeneralSettingsSectionProps {
	attributionMode: AttributionModeSection;
	corsOrigins: CorsOriginsSection;
	iosStoreLink: IosStoreLinkSection;
	androidStoreLink: AndroidStoreLinkSection;
	platforms: ("ios" | "android" | "any")[] | undefined;
}

export function GeneralSettingsSection({
	attributionMode,
	corsOrigins,
	iosStoreLink,
	androidStoreLink,
	platforms,
}: GeneralSettingsSectionProps) {
	const iosEnabled = platforms?.includes("ios") ?? false;
	const androidEnabled = platforms?.includes("android") ?? false;
	return (
		<AccordionItem value="general">
			<AccordionHeader>
				<AccordionTrigger>
					<div className="flex items-center gap-3">
						<div className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
							<Settings2 className="size-4" />
						</div>
						<div className="flex flex-col items-start">
							<span className="font-semibold">General Settings</span>
							<span className="text-xs text-base-content/50 font-normal">
								Attribution mode, CORS & store links
							</span>
						</div>
					</div>
				</AccordionTrigger>
			</AccordionHeader>
			<AccordionPanel>
				<div className="grid grid-cols-1 gap-6 pt-2 sm:grid-cols-2">
					<Field>
						<InfoLabel
							info={
								<div className="flex flex-col gap-2">
									<p>
										Controls how referrals are attributed for this app.{" "}
										<span className="font-bold text-secondary">
											Explicit code
										</span>{" "}
										requires the user to manually confirm a referral inside the
										app (via code or QR). This is deterministic,
										privacy-preserving, and the most reliable option.
									</p>
									<p>
										<span className="font-bold text-secondary">
											Time-window auto-claim
										</span>{" "}
										attempts attribution automatically within a short window
										after install. This is best-effort only and may fail or be
										skipped if attribution would be ambiguous.
									</p>
								</div>
							}
						>
							Attribution Mode
						</InfoLabel>

						<InputGroup className="flex items-center gap-3">
							<Select
								showArrow={false}
								value={attributionMode.value}
								className="appearance-none"
								onChange={(e) =>
									attributionMode.update(e.target.value as "code" | "window")
								}
							>
								<option value="code">Explicit code (recommended)</option>
								<option value="window">Time-window auto-claim</option>
							</Select>

							{attributionMode.isSaving && (
								<Loader2
									className="h-4 w-4 animate-spin text-base-content/40"
									data-slot="icon"
								/>
							)}
							{attributionMode.isSaved && (
								<Check className="h-4 w-4 " data-slot="icon" />
							)}
						</InputGroup>
					</Field>

					<Field>
						<InfoLabel info="Comma-separated list of origins allowed to access your referral links. Leave empty to allow all origins.">
							Allowed CORS Origins
						</InfoLabel>

						<InputGroup>
							<Input
								name="allowedCorsOrigins"
								value={corsOrigins.value}
								onChange={(e) => corsOrigins.update(e.target.value)}
								placeholder="https://example.com,https://app.example.com"
							/>

							{corsOrigins.isSaving && (
								<Loader2
									className="h-4 w-4 animate-spin text-base-content/40"
									data-slot="icon"
								/>
							)}
							{corsOrigins.isSaved && (
								<Check className="h-4 w-4" data-slot="icon" />
							)}
						</InputGroup>
					</Field>

					<Field>
						<InfoLabel
							info={
								iosEnabled
									? "Disable iOS platform to edit this field."
									: "The public App Store URL for your iOS app. Used for verification and referral redirects."
							}
						>
							iOS App Store Link
						</InfoLabel>

						<InputGroup>
							<Input
								name="iosStoreLink"
								value={iosStoreLink.value}
								onChange={(e) => iosStoreLink.update(e.target.value)}
								placeholder="https://apps.apple.com/app/id123456789"
								disabled={iosEnabled}
							/>

							{iosStoreLink.isSaving && (
								<Loader2
									className="h-4 w-4 animate-spin text-base-content/40"
									data-slot="icon"
								/>
							)}
							{iosStoreLink.isSaved && (
								<Check className="h-4 w-4" data-slot="icon" />
							)}
						</InputGroup>
					</Field>

					<Field>
						<InfoLabel
							info={
								androidEnabled
									? "Disable Android platform to edit this field."
									: "The public Google Play Store URL for your Android app. Used for verification and referral redirects."
							}
						>
							Google Play Store Link
						</InfoLabel>

						<InputGroup>
							<Input
								name="androidStoreLink"
								value={androidStoreLink.value}
								onChange={(e) => androidStoreLink.update(e.target.value)}
								placeholder="https://play.google.com/store/apps/details?id=com.example.app"
								disabled={androidEnabled}
							/>

							{androidStoreLink.isSaving && (
								<Loader2
									className="h-4 w-4 animate-spin text-base-content/40"
									data-slot="icon"
								/>
							)}
							{androidStoreLink.isSaved && (
								<Check className="h-4 w-4" data-slot="icon" />
							)}
						</InputGroup>
					</Field>
				</div>
			</AccordionPanel>
		</AccordionItem>
	);
}
